import { logger } from "@/utils/logger";
import { useSnackbar } from "@/components/common/Snackbar";
import { useSocialAuth } from "@/hooks/useSocialAuth";
import { useAuthStore } from "@/stores/authStore";
import { User } from "@/types/app_types";
import { cookieUtils } from "@/utils/cookie";
import { getGoogleAuthUrl } from "@/utils/googleAuth";
import { initKakao } from "@/utils/kakaoAuth";
import {
  openAuthPopup,
  waitForAuthMessage,
  isAuthSuccessMessage,
  isAuthErrorMessage,
  isAuthCancelledMessage,
} from "@/utils/socialAuthPopup";
import { useCallback, useEffect, useRef, useState } from "react";

// ========================================
// 타입 정의
// ========================================

/**
 * 소셜 로그인 결과
 */
interface SocialLoginResult {
  requiresAgreement: boolean;
  error?: string;
}

/**
 * 신규 회원가입에 필요한 임시 데이터
 */
export interface PendingSignupData {
  provider: "google" | "kakao";
  tempToken: string;
  user?: {
    provider: "google" | "kakao";
    email: string;
    name?: string;
  };
}

/**
 * useSocialLogin Hook의 반환 타입
 */
interface UseSocialLoginReturn {
  loading: boolean;
  pendingSignupData: PendingSignupData | null;
  handleGoogleSignIn: () => Promise<SocialLoginResult | undefined>;
  handleKakaoSignIn: () => Promise<SocialLoginResult | undefined>;
}

/**
 * Kakao SDK 응답 타입
 */
interface KakaoAuthResponse {
  access_token?: string;
  error?: string;
  error_description?: string;
}

// ========================================
// Hook 구현
// ========================================

/**
 * 소셜 로그인 (Google, Kakao) 처리 Hook
 *
 * @param onSuccessCallback - 로그인 성공 시 실행할 콜백
 * @returns 로딩 상태, 임시 데이터, 로그인 핸들러 함수들
 */
export const useSocialLogin = (
  onSuccessCallback: () => void,
): UseSocialLoginReturn => {
  const [loading, setLoading] = useState(false);
  const [pendingSignupData, setPendingSignupData] =
    useState<PendingSignupData | null>(null);
  const { showSnackbar } = useSnackbar();
  const { setUser } = useAuthStore();
  const { kakaoLogin } = useSocialAuth();

  // 팝업 상태 체크를 위한 ref
  const popupCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup functions for Kakao and Google auth
  const kakaoCleanupRef = useRef<(() => void) | null>(null);
  const googleCleanupRef = useRef<(() => void) | null>(null);

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      if (popupCheckIntervalRef.current) {
        clearInterval(popupCheckIntervalRef.current);
      }
      // Call any pending cleanup functions
      if (kakaoCleanupRef.current) {
        kakaoCleanupRef.current();
        kakaoCleanupRef.current = null;
      }
      if (googleCleanupRef.current) {
        googleCleanupRef.current();
        googleCleanupRef.current = null;
      }
    };
  }, []);

  const handleSuccess = useCallback(() => {
    if (onSuccessCallback) {
      onSuccessCallback();
    }
  }, [onSuccessCallback]);

  // ========================================
  // Google 로그인 핸들러
  // ========================================

  const handleGoogleSignIn = async (): Promise<
    SocialLoginResult | undefined
  > => {
    setLoading(true);

    let isAuthCompleted = false;

    try {
      // 1. Google OAuth URL 생성
      const googleAuthUrl = await getGoogleAuthUrl();

      // 2. 팝업 열기
      const popup = openAuthPopup({
        url: googleAuthUrl,
        name: "google-login-popup",
      });

      if (!popup) {
        showSnackbar("Popup was blocked. Please allow popups for this site.");
        setLoading(false);
        return { requiresAgreement: false, error: "POPUP_BLOCKED" };
      }

      // 3. 포커스 이벤트로 팝업 닫힘 간접 감지
      const handleWindowFocus = () => {
        if (!isAuthCompleted) {
          setTimeout(() => {
            if (!isAuthCompleted) {
              setLoading(false);
              showSnackbar("Google login cancelled");
            }
          }, 1000);
        }
      };

      const googleCleanup = () => {
        window.removeEventListener("focus", handleWindowFocus);
      };

      // Store cleanup function for unmount
      googleCleanupRef.current = googleCleanup;

      window.addEventListener("focus", handleWindowFocus);

      try {
        // 4. 인증 메시지 대기 (타입 안전성 강화)
        const message = await waitForAuthMessage(
          ["GOOGLE_AUTH_SUCCESS", "GOOGLE_AUTH_ERROR", "GOOGLE_AUTH_CANCELLED"],
          60000,
        );

        isAuthCompleted = true;
        window.removeEventListener("focus", handleWindowFocus);

        // 5-1. 성공 메시지 처리
        if (isAuthSuccessMessage(message)) {
          const { requiresAgreement, tempData, user } = message.payload;

          // 신규 회원 - 약관 동의 필요
          if (requiresAgreement && tempData) {
            setPendingSignupData({
              provider: "google",
              tempToken: tempData.tempToken,
            });
            return { requiresAgreement: true };
          }

          // 기존 회원 - 로그인 성공
          if (user) {
            setUser(user as User);
          }

          showSnackbar("Google login succeeded", {
            showCheckIcon: true,
            duration: 3000,
          });

          handleSuccess();
          return { requiresAgreement: false };
        }

        // 5-2. 에러 메시지 처리
        if (isAuthErrorMessage(message)) {
          logger.error("❌ Google auth error:", message.payload.error);
          showSnackbar(message.payload.error || "Google login failed");
          return { requiresAgreement: false, error: message.payload.error };
        }

        // 5-3. 취소 메시지 처리
        if (isAuthCancelledMessage(message)) {
          showSnackbar("Google login cancelled");
          return { requiresAgreement: false };
        }

        // 예상치 못한 메시지 타입
        showSnackbar("Unexpected authentication response");
        return { requiresAgreement: false, error: "UNEXPECTED_MESSAGE" };
      } catch (error) {
        isAuthCompleted = true;
        window.removeEventListener("focus", handleWindowFocus);

        if (error instanceof Error && error.message.includes("시간이 초과")) {
          logger.error("⏱️ Timeout while waiting for Google login");
          showSnackbar("Timeout while waiting for Google login response.");
        } else {
          logger.error("❌ Error during Google login:", { error: error });
          showSnackbar("Error occurred during Google login.");
        }
        return { requiresAgreement: false, error: "TIMEOUT_OR_ERROR" };
      }
    } catch (error) {
      logger.error("❌ Fatal Google sign in error:", { error: error });
      showSnackbar("Error occurred during Google login.");
      return { requiresAgreement: false, error: "AUTH_ERROR" };
    } finally {
      setLoading(false);
      sessionStorage.removeItem("google_pkce_verifier");
      // Cleanup event listeners
      if (googleCleanupRef.current) {
        googleCleanupRef.current();
        googleCleanupRef.current = null;
      }
    }
  };

  // ========================================
  // Kakao 로그인 핸들러
  // ========================================

  const handleKakaoSignIn = async (): Promise<
    SocialLoginResult | undefined
  > => {
    setLoading(true);

    // Kakao SDK 로드 및 초기화 대기
    if (!window.Kakao?.Auth?.login) {
      const isInitialized = await initKakao();
      if (!isInitialized || !window.Kakao?.Auth?.login) {
        logger.error("Kakao SDK not loaded");
        showSnackbar(
          "Kakao SDK is not loaded. Please refresh the page and try again.",
        );
        setLoading(false);
        return { requiresAgreement: false, error: "SDK_NOT_LOADED" };
      }
    }

    let isCompleted = false;
    let focusCheckInterval: NodeJS.Timeout | null = null;
    let lastBlurTime = 0;

    // 윈도우 포커스 이벤트로 팝업 닫힘 간접 감지
    const handleWindowFocus = () => {
      if (!isCompleted && lastBlurTime > 0) {
        const timeDiff = Date.now() - lastBlurTime;
        if (timeDiff > 1000) {
          setTimeout(() => {
            if (!isCompleted && !window.Kakao?.Auth?.getAccessToken()) {
              isCompleted = true;
              setLoading(false);
              showSnackbar("Kakao login cancelled");
              cleanup();
            }
          }, 500);
        }
      }
    };

    const handleWindowBlur = () => {
      lastBlurTime = Date.now();
    };

    const cleanup = () => {
      if (focusCheckInterval) {
        clearInterval(focusCheckInterval);
        focusCheckInterval = null;
      }
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("blur", handleWindowBlur);
    };

    // Store cleanup function for unmount
    kakaoCleanupRef.current = cleanup;

    try {
      // 1. 포커스 이벤트 리스너 등록
      window.addEventListener("blur", handleWindowBlur);
      window.addEventListener("focus", handleWindowFocus);

      // 주기적으로 상태 체크 (폴백)
      focusCheckInterval = setInterval(() => {
        if (!isCompleted && !document.hidden && lastBlurTime > 0) {
          const timeDiff = Date.now() - lastBlurTime;
          if (timeDiff > 3000) {
            isCompleted = true;
            setLoading(false);
            showSnackbar("Timeout while waiting for Kakao login response.");
            cleanup();
          }
        }
      }, 1000);

      // 2. 카카오 SDK로 로그인 (팝업)
      const kakaoResponse = await new Promise<{
        success: boolean;
        accessToken?: string;
        error?: string;
      }>((resolve) => {
        const loginTimeout = setTimeout(() => {
          if (!isCompleted) {
            isCompleted = true;
            resolve({
              success: false,
              error: "Timeout while waiting for Kakao login response.",
            });
          }
        }, 60000);

        window.Kakao.Auth.login({
          scope: "account_email, profile_image",
          success: (response: KakaoAuthResponse) => {
            clearTimeout(loginTimeout);
            isCompleted = true;
            resolve({
              success: true,
              accessToken: response.access_token,
            });
          },
          fail: (error: KakaoAuthResponse) => {
            clearTimeout(loginTimeout);
            isCompleted = true;
            logger.error("❌ Kakao login failed:", { error: error });

            if (error.error === "user_cancelled" || !error.error) {
              resolve({
                success: false,
                error: "Kakao login cancelled",
              });
            } else {
              resolve({
                success: false,
                error: error.error_description || "Kakao login failed",
              });
            }
          },
        });
      });

      cleanup();

      if (!kakaoResponse.success || !kakaoResponse.accessToken) {
        showSnackbar(kakaoResponse.error || "Kakao login cancelled");
        setLoading(false);
        return { requiresAgreement: false, error: "USER_CANCELLED" };
      }

      // 3. 백엔드로 카카오 액세스 토큰 전송
      const response = await kakaoLogin(kakaoResponse.accessToken);

      if (!response.success) {
        logger.error("❌ Backend kakao login failed:", response.error);
        showSnackbar(response.error || "Kakao login failed");
        return { requiresAgreement: false, error: response.error };
      }

      // 4-1. 약관 동의 필요 (신규 회원)
      if (response.requiresAgreement && response.tempData) {
        setPendingSignupData({
          provider: "kakao",
          tempToken: response.tempData.tempToken,
        });
        return { requiresAgreement: true };
      }

      // 4-2. 로그인 성공 (기존 회원)
      if (response.user && response.tokens) {
        cookieUtils.set("accessToken", response.tokens.accessToken);
        cookieUtils.set("refreshToken", response.tokens.refreshToken);
        setUser(response.user);

        showSnackbar("Kakao login succeeded", {
          showCheckIcon: true,
          duration: 3000,
        });

        handleSuccess();
        return { requiresAgreement: false };
      }

      // 예상치 못한 응답
      logger.error("❌ Unexpected response format from backend");
      showSnackbar("Unexpected response format");
      return { requiresAgreement: false, error: "UNEXPECTED_RESPONSE" };
    } catch (error) {
      logger.error("❌ Fatal Kakao sign in error:", { error: error });
      showSnackbar("Error occurred during Kakao login.");
      return { requiresAgreement: false, error: "AUTH_ERROR" };
    } finally {
      setLoading(false);
      cleanup();
      // Clear the ref after cleanup
      kakaoCleanupRef.current = null;
    }
  };

  return {
    loading,
    pendingSignupData,
    handleGoogleSignIn,
    handleKakaoSignIn,
  };
};
